import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadSmartCards,
  toggleReportFavorite,
} from "../../Redux/Actions/ActionStatistics";
import "./SmartCardsPanel.css";

const STORAGE_KEY = "miamed_smart_cards_collapsed";

/**
 * FE-4 — Smart Cards Panel (collapsible)
 * Shows recent reports and favorites as quick-access cards.
 *
 * Props:
 *   onSelect   — callback(reportKey) to navigate to a report
 *   activeReport — currently active report key
 *   translate  — i18n function
 *   mode       — optional "recent" | "favorites" to show a single section (dashboard widgets)
 */
function SmartCardsPanel({ onSelect, activeReport, translate, mode }) {
  const dispatch = useDispatch();
  const smartCards = useSelector((s) => s.statisticss.smartCards) || {};
  const recent = smartCards.recent || [];
  const favorites = smartCards.favorites || [];

  /* mode splits the panel into standalone dashboard widgets */
  const showFavorites = mode !== "recent";
  const showRecent = mode !== "favorites";

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    dispatch(loadSmartCards());
  }, [dispatch]);

  const handleToggleFav = (reportKey) => {
    dispatch(toggleReportFavorite(reportKey));
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
  };

  const hasContent =
    (showFavorites && favorites.length > 0) ||
    (showRecent && recent.length > 0);

  if (!hasContent) {
    return null;
  }

  return (
    <div className={`smart-cards-panel${collapsed ? " collapsed" : ""}`}>
      <div className="smart-cards-panel-header" onClick={toggleCollapse}>
        <span className="smart-cards-panel-title">
          <span className="las la-bolt"></span>
          {translate("smart_cards_title")}
        </span>
        <span className={`las ${collapsed ? "la-angle-down" : "la-angle-up"}`}></span>
      </div>

      {!collapsed && (
        <div className="smart-cards-panel-body">
          {/* Favorites section */}
          {showFavorites && favorites.length > 0 && (
            <div className="smart-cards-section">
              <div className="smart-cards-label">
                <span className="las la-star"></span>
                {translate("smart_cards_favorites")}
              </div>
              <div className="smart-cards-row">
                {favorites.map((key) => (
                  <div
                    key={key}
                    className={`smart-card${activeReport === key ? " active" : ""}`}
                    onClick={() => onSelect && onSelect(key)}
                  >
                    <span className="smart-card-name">{translate(key)}</span>
                    <span
                      className="smart-card-fav active"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFav(key);
                      }}
                      title={translate("smart_cards_remove_fav")}
                    >
                      <span className="las la-star"></span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent section */}
          {showRecent && recent.length > 0 && (
            <div className="smart-cards-section">
              <div className="smart-cards-label">
                <span className="las la-clock"></span>
                {translate("smart_cards_recent")}
              </div>
              <div className="smart-cards-row">
                {recent.map((key) => {
                  const isFav = favorites.includes(key);
                  return (
                    <div
                      key={key}
                      className={`smart-card${activeReport === key ? " active" : ""}`}
                      onClick={() => onSelect && onSelect(key)}
                    >
                      <span className="smart-card-name">{translate(key)}</span>
                      <span
                        className={`smart-card-fav${isFav ? " active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFav(key);
                        }}
                        title={
                          isFav
                            ? translate("smart_cards_remove_fav")
                            : translate("smart_cards_add_fav")
                        }
                      >
                        <span className={`las ${isFav ? "la-star" : "la-star-o"}`}></span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SmartCardsPanel;
